import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, User, ArrowRight } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useUserMode } from '@/stores/userModeStore';
import { useNavigate } from 'react-router-dom';

export const SecurityLevelPicker = () => {
  const { profile, isAdmin, loading } = useAuth();
  const { setMode } = useUserMode();
  const navigate = useNavigate();

  // Redirect non-admin users using useEffect to avoid navigation during render
  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/staff-dashboard', { replace: true });
    }
  }, [isAdmin, loading, navigate]);

  // Show loading while auth is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Don't render anything while redirecting
  if (!isAdmin) {
    return null;
  }

  const handleModeSelection = (mode: 'admin' | 'staff') => {
    setMode(mode);
    if (mode === 'admin') {
      navigate('/admin-dashboard');
    } else {
      navigate('/staff-dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold">Choose Your Access Level</h1>
          <p className="text-muted-foreground">
            Select the security level for this session. You can change this anytime.
          </p>
          <Badge variant="outline" className="bg-primary/10">
            Welcome, {profile?.full_name || profile?.email}
          </Badge>
        </div>

        {/* Security Level Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Staff Access */}
          <Card className="cursor-pointer hover:shadow-lg transition-all hover:scale-105 border-2 hover:border-primary/50">
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-xl">Staff Access</CardTitle>
              <p className="text-sm text-muted-foreground">
                Access basic operational features
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Available Features:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Order management</li>
                  <li>• Inventory lookup</li>
                  <li>• Basic reporting</li>
                  <li>• Employee time tracking</li>
                </ul>
              </div>
              <Button 
                onClick={() => handleModeSelection('staff')}
                className="w-full"
                variant="outline"
              >
                Continue as Staff
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Admin Access */}
          <Card className="cursor-pointer hover:shadow-lg transition-all hover:scale-105 border-2 hover:border-red-500/50">
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-xl">Admin Access</CardTitle>
              <p className="text-sm text-muted-foreground">
                Full system access and management
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Additional Features:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• User management</li>
                  <li>• System configuration</li>
                  <li>• Analytics & reporting</li>
                  <li>• Security settings</li>
                  <li>• Data synchronization</li>
                </ul>
              </div>
              <Button 
                onClick={() => handleModeSelection('admin')}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                Continue as Admin
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Security Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="space-y-1">
              <h4 className="font-medium text-amber-800">Security Notice</h4>
              <p className="text-sm text-amber-700">
                Your access level affects which features and data you can view. 
                All actions are logged for security purposes. You can switch between 
                modes at any time using the mode switcher in the navigation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};